/**
 * Input — keyboard + drag-to-look mouse.
 *
 * Movement: WASD OR arrow keys. Run: Shift. Jump: Space.
 * Look: press-and-hold left mouse button anywhere on the canvas, then drag.
 *       Cursor remains visible at all times; release to interact with the UI.
 * Chat: T to open, Enter to send, Escape to close.
 * Emotes: digits 1..9.
 * Camera mode: V toggles first/third person.
 */

import { AnimState } from '@shared/protocol';

export interface InputState {
  forward: boolean;
  back: boolean;
  left: boolean;
  right: boolean;
  jump: boolean;
  run: boolean;
  yawDelta: number;
  pitchDelta: number;
  /** True while the user is holding the left mouse button to drag-look. */
  dragging: boolean;
}

export type InputEvent =
  | { type: 'emote'; anim: AnimState }
  | { type: 'chat-open' }
  | { type: 'chat-close' }
  | { type: 'toggle-camera' }
  | { type: 'open-spawn-picker' };

const LOOK_SENSITIVITY = 0.004;

export class Input {
  readonly state: InputState = {
    forward: false,
    back: false,
    left: false,
    right: false,
    jump: false,
    run: false,
    yawDelta: 0,
    pitchDelta: 0,
    dragging: false
  };

  private listeners: ((e: InputEvent) => void)[] = [];
  private chatOpen = false;
  private canvas?: HTMLCanvasElement;
  private lastMouseX = 0;
  private lastMouseY = 0;

  attach(canvas: HTMLCanvasElement): void {
    this.canvas = canvas;
    canvas.style.cursor = 'grab';

    window.addEventListener('keydown', this.onKeyDown);
    window.addEventListener('keyup', this.onKeyUp);
    canvas.addEventListener('mousedown', this.onMouseDown);
    window.addEventListener('mouseup', this.onMouseUp);
    window.addEventListener('mousemove', this.onMouseMove);
    canvas.addEventListener('contextmenu', this.onContextMenu);
  }

  detach(): void {
    window.removeEventListener('keydown', this.onKeyDown);
    window.removeEventListener('keyup', this.onKeyUp);
    window.removeEventListener('mouseup', this.onMouseUp);
    window.removeEventListener('mousemove', this.onMouseMove);
    if (this.canvas) {
      this.canvas.removeEventListener('mousedown', this.onMouseDown);
      this.canvas.removeEventListener('contextmenu', this.onContextMenu);
    }
  }

  on(listener: (e: InputEvent) => void): () => void {
    this.listeners.push(listener);
    return () => {
      this.listeners = this.listeners.filter((l) => l !== listener);
    };
  }

  private emit(e: InputEvent): void {
    for (const l of this.listeners) l(e);
  }

  /** Consume accumulated mouse delta and return it. */
  consumeMouseDelta(): { yaw: number; pitch: number } {
    const out = { yaw: this.state.yawDelta, pitch: this.state.pitchDelta };
    this.state.yawDelta = 0;
    this.state.pitchDelta = 0;
    return out;
  }

  setChatOpen(open: boolean): void {
    this.chatOpen = open;
    if (open) {
      this.state.forward = this.state.back = false;
      this.state.left = this.state.right = false;
      this.state.run = false;
      this.state.jump = false;
      this.state.dragging = false;
      if (this.canvas) this.canvas.style.cursor = 'default';
    } else if (this.canvas) {
      this.canvas.style.cursor = 'grab';
    }
  }

  /** Back-compat stub so SpawnPicker.onSpawn doesn't need to know about pointer-lock. */
  engagePointerLock(): void {
    // no-op in drag-to-look mode
  }

  private onKeyDown = (e: KeyboardEvent): void => {
    if (this.chatOpen) {
      if (e.key === 'Escape') {
        this.emit({ type: 'chat-close' });
      }
      return;
    }
    switch (e.code) {
      case 'KeyW':
      case 'ArrowUp':
        this.state.forward = true; e.preventDefault(); break;
      case 'KeyS':
      case 'ArrowDown':
        this.state.back = true; e.preventDefault(); break;
      case 'KeyA':
      case 'ArrowLeft':
        this.state.left = true; e.preventDefault(); break;
      case 'KeyD':
      case 'ArrowRight':
        this.state.right = true; e.preventDefault(); break;
      case 'Space': this.state.jump = true; e.preventDefault(); break;
      case 'ShiftLeft':
      case 'ShiftRight': this.state.run = true; break;
      case 'KeyT':
      case 'Slash':
        e.preventDefault();
        this.emit({ type: 'chat-open' });
        break;
      case 'KeyV':
        this.emit({ type: 'toggle-camera' });
        break;
      case 'KeyM':
        this.emit({ type: 'open-spawn-picker' });
        break;
      case 'Digit1':
      case 'Digit2':
      case 'Digit3':
      case 'Digit4':
      case 'Digit5':
      case 'Digit6':
      case 'Digit7':
      case 'Digit8':
      case 'Digit9': {
        const idx = Number(e.code.slice(-1));
        const map: AnimState[] = [
          AnimState.Waving,
          AnimState.Dancing,
          AnimState.Sitting,
          AnimState.Headbang,
          AnimState.Peace,
          AnimState.Clap,
          AnimState.HandsUp,
          AnimState.Lying,
          AnimState.Idle
        ];
        this.emit({ type: 'emote', anim: map[idx - 1] });
        break;
      }
    }
  };

  private onKeyUp = (e: KeyboardEvent): void => {
    switch (e.code) {
      case 'KeyW':
      case 'ArrowUp':
        this.state.forward = false; break;
      case 'KeyS':
      case 'ArrowDown':
        this.state.back = false; break;
      case 'KeyA':
      case 'ArrowLeft':
        this.state.left = false; break;
      case 'KeyD':
      case 'ArrowRight':
        this.state.right = false; break;
      case 'Space': this.state.jump = false; break;
      case 'ShiftLeft':
      case 'ShiftRight': this.state.run = false; break;
    }
  };

  private onMouseDown = (e: MouseEvent): void => {
    if (this.chatOpen) return;
    if (e.button !== 0) return; // left button only
    this.state.dragging = true;
    this.lastMouseX = e.clientX;
    this.lastMouseY = e.clientY;
    if (this.canvas) this.canvas.style.cursor = 'grabbing';
  };

  private onMouseUp = (e: MouseEvent): void => {
    if (e.button !== 0) return;
    this.state.dragging = false;
    if (this.canvas && !this.chatOpen) this.canvas.style.cursor = 'grab';
  };

  private onMouseMove = (e: MouseEvent): void => {
    if (!this.state.dragging) return;
    const dx = e.clientX - this.lastMouseX;
    const dy = e.clientY - this.lastMouseY;
    this.lastMouseX = e.clientX;
    this.lastMouseY = e.clientY;
    this.state.yawDelta -= dx * LOOK_SENSITIVITY;
    this.state.pitchDelta -= dy * LOOK_SENSITIVITY;
  };

  private onContextMenu = (e: Event): void => {
    e.preventDefault();
  };
}
